import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Plus, RefreshCw, Save, Trash2 } from "lucide-react";

import {
  getKostenContent,
  saveKostenItem,
  deleteKostenItem,
  saveKostenSettings,
  type KostenContent,
} from "@/lib/kosten.functions";

export const Route = createFileRoute("/_authenticated/admin-kosten")({
  component: KostenBeheerPage,
  head: () => ({
    meta: [
      { title: "Beheer — Prijzen | Fedor Timmerwerken" },
      { name: "description", content: "Beheerdersweergave om richtprijzen aan te passen." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

type ItemForm = {
  id: string | null;
  slug: string;
  title: string;
  prijs: string;
  prijsNoot: string;
  factoren: string;
  sortOrder: number;
};

const toForm = (item: KostenContent["items"][number]): ItemForm => ({
  id: item.id,
  slug: item.slug,
  title: item.title,
  prijs: item.prijs ?? "",
  prijsNoot: item.prijsNoot ?? "",
  factoren: item.factoren.join("\n"),
  sortOrder: item.sortOrder,
});

const inputClass =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const labelClass = "text-xs font-semibold uppercase tracking-wider text-muted-foreground";
const buttonClass =
  "inline-flex min-h-10 items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-50";
const primaryButtonClass =
  "inline-flex min-h-10 items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50";

function KostenBeheerPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const loadFn = useServerFn(getKostenContent);
  const saveItemFn = useServerFn(saveKostenItem);
  const deleteItemFn = useServerFn(deleteKostenItem);
  const saveSettingsFn = useServerFn(saveKostenSettings);

  const content = useQuery<KostenContent>({
    queryKey: ["kosten-content"],
    queryFn: () => loadFn(),
  });

  const [forms, setForms] = useState<ItemForm[]>([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [faq, setFaq] = useState<{ vraag: string; antwoord: string }[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!content.data) return;
    setForms(content.data.items.map(toForm));
    setDisclaimer(content.data.disclaimer);
    setFaq(content.data.faq.map((entry) => ({ ...entry })));
  }, [content.data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["kosten-content"] });
    navigate({ to: "/admin-kosten" });
  };

  const saveItem = useMutation({
    mutationFn: (form: ItemForm) =>
      saveItemFn({
        data: {
          ...(form.id ? { id: form.id } : {}),
          slug: form.slug.trim(),
          title: form.title.trim(),
          prijs: form.prijs.trim() || null,
          prijs_noot: form.prijsNoot.trim() || null,
          factoren: form.factoren
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean),
          sort_order: form.sortOrder,
        },
      }),
    onSuccess: () => {
      setMessage("Dienst opgeslagen.");
      invalidate();
    },
    onError: (error: Error) => setMessage(`Opslaan mislukt: ${error.message}`),
  });

  const removeItem = useMutation({
    mutationFn: (id: string) => deleteItemFn({ data: { id } }),
    onSuccess: () => {
      setMessage("Dienst verwijderd.");
      invalidate();
    },
    onError: (error: Error) => setMessage(`Verwijderen mislukt: ${error.message}`),
  });

  const saveSettings = useMutation({
    mutationFn: () =>
      saveSettingsFn({
        data: {
          disclaimer: disclaimer.trim(),
          faq: faq
            .map((entry) => ({ vraag: entry.vraag.trim(), antwoord: entry.antwoord.trim() }))
            .filter((entry) => entry.vraag && entry.antwoord),
        },
      }),
    onSuccess: () => {
      setMessage("Toelichting en vragen opgeslagen.");
      invalidate();
    },
    onError: (error: Error) => setMessage(`Opslaan mislukt: ${error.message}`),
  });

  const updateForm = (index: number, patch: Partial<ItemForm>) =>
    setForms((prev) => prev.map((form, i) => (i === index ? { ...form, ...patch } : form)));

  const addItem = () =>
    setForms((prev) => [
      ...prev,
      {
        id: null,
        slug: "",
        title: "",
        prijs: "",
        prijsNoot: "",
        factoren: "",
        sortOrder: (prev.at(-1)?.sortOrder ?? 0) + 10,
      },
    ]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <header className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Prijzen beheren</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Wijzigingen zijn direct zichtbaar op de kostenpagina.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin" className={buttonClass}>
            <ArrowLeft className="h-4 w-4" /> Aanvragen
          </Link>
          <button type="button" onClick={() => content.refetch()} className={buttonClass}>
            <RefreshCw className={content.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            Vernieuwen
          </button>
        </div>
      </header>

      {message && (
        <p className="mb-6 rounded-md border border-border bg-secondary/40 px-4 py-3 text-sm">
          {message}
        </p>
      )}

      {content.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <section className="space-y-5">
            {forms.map((form, index) => (
              <article
                key={form.id ?? `new-${index}`}
                className="rounded-2xl border border-border/60 bg-card p-5"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className={labelClass}>Titel</span>
                    <input
                      className={`mt-1 ${inputClass}`}
                      value={form.title}
                      onChange={(e) => updateForm(index, { title: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>Slug (dienst-URL)</span>
                    <input
                      className={`mt-1 ${inputClass}`}
                      value={form.slug}
                      onChange={(e) => updateForm(index, { slug: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>Richtprijs (leeg = op aanvraag)</span>
                    <input
                      className={`mt-1 ${inputClass}`}
                      value={form.prijs}
                      onChange={(e) => updateForm(index, { prijs: e.target.value })}
                    />
                  </label>
                  <label className="block">
                    <span className={labelClass}>Sorteervolgorde</span>
                    <input
                      type="number"
                      className={`mt-1 ${inputClass}`}
                      value={form.sortOrder}
                      onChange={(e) => updateForm(index, { sortOrder: Number(e.target.value) })}
                    />
                  </label>
                </div>

                <label className="mt-4 block">
                  <span className={labelClass}>Toelichting bij de prijs</span>
                  <textarea
                    rows={2}
                    className={`mt-1 ${inputClass}`}
                    value={form.prijsNoot}
                    onChange={(e) => updateForm(index, { prijsNoot: e.target.value })}
                  />
                </label>

                <label className="mt-4 block">
                  <span className={labelClass}>Prijs hangt af van (één per regel)</span>
                  <textarea
                    rows={4}
                    className={`mt-1 ${inputClass}`}
                    value={form.factoren}
                    onChange={(e) => updateForm(index, { factoren: e.target.value })}
                  />
                </label>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={primaryButtonClass}
                    disabled={saveItem.isPending}
                    onClick={() => {
                      setMessage(null);
                      saveItem.mutate(form);
                    }}
                  >
                    <Save className="h-4 w-4" /> Opslaan
                  </button>
                  {form.id && (
                    <button
                      type="button"
                      className={buttonClass}
                      disabled={removeItem.isPending}
                      onClick={() => {
                        if (!window.confirm(`"${form.title}" verwijderen?`)) return;
                        setMessage(null);
                        removeItem.mutate(form.id as string);
                      }}
                    >
                      <Trash2 className="h-4 w-4" /> Verwijderen
                    </button>
                  )}
                </div>
              </article>
            ))}

            <button type="button" onClick={addItem} className={buttonClass}>
              <Plus className="h-4 w-4" /> Dienst toevoegen
            </button>
          </section>

          <section className="mt-10 rounded-2xl border border-border/60 bg-card p-5">
            <h2 className="font-heading text-xl font-semibold">Toelichting en vragen</h2>

            <label className="mt-4 block">
              <span className={labelClass}>Disclaimer onder de prijskaarten</span>
              <textarea
                rows={3}
                className={`mt-1 ${inputClass}`}
                value={disclaimer}
                onChange={(e) => setDisclaimer(e.target.value)}
              />
            </label>

            <div className="mt-6 space-y-4">
              {faq.map((entry, index) => (
                <div key={index} className="rounded-xl border border-border/60 p-4">
                  <label className="block">
                    <span className={labelClass}>Vraag</span>
                    <input
                      className={`mt-1 ${inputClass}`}
                      value={entry.vraag}
                      onChange={(e) =>
                        setFaq((prev) =>
                          prev.map((item, i) =>
                            i === index ? { ...item, vraag: e.target.value } : item,
                          ),
                        )
                      }
                    />
                  </label>
                  <label className="mt-3 block">
                    <span className={labelClass}>Antwoord</span>
                    <textarea
                      rows={3}
                      className={`mt-1 ${inputClass}`}
                      value={entry.antwoord}
                      onChange={(e) =>
                        setFaq((prev) =>
                          prev.map((item, i) =>
                            i === index ? { ...item, antwoord: e.target.value } : item,
                          ),
                        )
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className={`mt-3 ${buttonClass}`}
                    onClick={() => setFaq((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-4 w-4" /> Vraag verwijderen
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                className={buttonClass}
                onClick={() => setFaq((prev) => [...prev, { vraag: "", antwoord: "" }])}
              >
                <Plus className="h-4 w-4" /> Vraag toevoegen
              </button>
              <button
                type="button"
                className={primaryButtonClass}
                disabled={saveSettings.isPending}
                onClick={() => {
                  setMessage(null);
                  saveSettings.mutate();
                }}
              >
                <Save className="h-4 w-4" /> Opslaan
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
